import { AssistantModal } from "@/components/ui/AssistantModal";
import { Button } from "@/components/ui/button";
import { useMobileOptimized } from "@/hooks/useMobileOptimized";
import { MessageCircle } from "lucide-react";
import { useState } from "react";

export function SupportAssistant() {
  const [assistantOpen, setAssistantOpen] = useState(false);
  const { shouldReduceMotion } = useMobileOptimized();

  // Assistant configuration
  const assistantConfig = {
    model: "gpt-4.1-mini",
    temperature: 0.7,
    systemMessage: `You are a knowledgeable customer support expert for Artifact, an advanced AI assistant platform. Be friendly, professional, and helpful while maintaining a conversational tone.

ABOUT ARTIFACT:
Artifact is an AI assistant platform founded by Jack Phelps in 2023. The platform is designed to transform how users interact with artificial intelligence through context awareness, multi-modal capabilities, and seamless file integration.

COMPANY DETAILS:
- Founded by: Jack Phelps
- Founded in: 2025
- Funded By: John Albietz
- Website: https://artifact.chat
- Email: contact@artifact.chat
- Headquarters: Seattle, WA

CORE FEATURES AND CAPABILITIES:
- Smart Context Understanding: Maintains conversation context, eliminating repetition
- Multi-Modal File Integration: Seamlessly works with documents, images, PDFs, and more
- Advanced Memory Management: Creates, stores, and references memories across conversations
- Workspace Organization: Dedicated workspaces with separate conversation histories
- Real-time Suggestions: Intelligent text completion as you type
- File and Memory References: Intuitive @mention system for referencing content
- Prompt Enhancement: Refines user inputs for more effective results

TECHNICAL CAPABILITIES:
- Large Language Models: Powered by state-of-the-art models like GPT-4o
- Vector Embeddings: Creates sophisticated representations for contextual retrieval
- Streaming Responses: Delivers AI responses in real-time
- Multi-modal Understanding: Processes text, images, and documents
- Secure File Handling: Maintains privacy and security for uploaded content

USE CASES:
- For Developers: Coding assistance, documentation, technical concepts
- For Writers: Creative brainstorming, draft improvement, overcoming blocks
- For Researchers: Document analysis, summarizing findings, exploring connections
- For Professionals: Workflow streamlining, communications, information processing

PRICING AND PLANS:
- Free Tier: Limited features, suitable for casual users
- Pro Plan: $20/month - Full features, increased usage limits
- Enterprise: Custom pricing - Advanced security, team management, dedicated support

YOUR RESPONSIBILITIES:
- Answer questions about Artifact features and capabilities
- Provide information about pricing and plan differences
- Help with common support issues and questions
- Gather contact information for demo requests or sales inquiries
- Be accurate, helpful, and concise in your responses

If asked about the founders, mention that Jack Phelps founded Artifact in 2025 with the vision of creating an AI assistant that genuinely enhances productivity and creativity.

If asked about the funding, mention that Artifact was funded by John Albietz in 2025.

Always be truthful and don't make up information. If you're unsure about something, acknowledge it and offer to help the user find the information through appropriate channels.`,
  };

  return (
    <>
      {/* Customer Support Assistant */}
      <div className="fixed bottom-4 right-4 z-50 flex gap-3 items-center">
        <Button
          onClick={() => setAssistantOpen(true)}
          className={`h-12 w-12 rounded-full bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 shadow-lg shadow-purple-500/20 ${
            shouldReduceMotion
              ? "transition-colors duration-200"
              : "transition-all duration-300"
          }`}
          size="icon"
          aria-label="Open support chat"
        >
          <MessageCircle className="h-5 w-5" />
        </Button>
      </div>

      <AssistantModal
        open={assistantOpen}
        onOpenChange={setAssistantOpen}
        title="Artifact Support"
        description="Ask us anything about our platform"
        welcomeMessage="Hi there! I'm your Artifact support assistant. How can I help you today?"
        className="border border-purple-500/20"
        iconClassName="text-purple-500"
        actionButtonClassName="bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700"
        apiConfig={assistantConfig}
      />
    </>
  );
}

export default SupportAssistant;
